import { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Portal,
} from '@valwin/facto/components';

import { SubmittingOverlay } from './SubmittingOverlay';

type ShareModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  form: ReactNode;
  preview: ReactNode;
  onSubmit: () => void;
  mutationStatus?: 'idle' | 'pending' | 'success' | 'error';
};

export function ShareModal({
  open,
  onClose,
  title,
  form,
  preview,
  onSubmit,
  mutationStatus = 'idle',
}: ShareModalProps) {
  const { t } = useTranslation();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // React events cross the portal, so the submit would also reach a form wrapping the share trigger
    event.stopPropagation();
    onSubmit();
  };

  const onOpenChange = (event: { open: boolean }) => {
    if (!event.open) {
      onClose();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxWidth="4xl" bg="transparent" boxShadow="none">
            <Flex
              gap="4"
              align={{ base: 'center', md: 'stretch' }}
              flexDirection={{ base: 'column', md: 'row' }}
            >
              <Flex
                flexDirection="column"
                flex="2"
                bg="white"
                borderRadius="lg"
                p="6"
                boxShadow="lg"
                position="relative"
              >
                <SubmittingOverlay
                  status={mutationStatus}
                  onSuccess={onClose}
                />

                <Flex justify="space-between" align="center" mb="6">
                  <Dialog.Title>{title}</Dialog.Title>
                  <Dialog.CloseTrigger asChild>
                    <CloseButton />
                  </Dialog.CloseTrigger>
                </Flex>

                <Flex asChild flexDirection="column" flex={1}>
                  <form onSubmit={handleSubmit} noValidate>
                    <Box flex={1}>{form}</Box>

                    <Flex justify="flex-end" gap="3" mt="6">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={mutationStatus === 'pending'}
                      >
                        {t('common cancel')}
                      </Button>
                      <Button
                        type="submit"
                        disabled={mutationStatus === 'pending'}
                      >
                        {t('common publish')}
                      </Button>
                    </Flex>
                  </form>
                </Flex>
              </Flex>

              <Flex
                flexDirection="column"
                flex="3"
                minW="300px"
                bg="white"
                borderRadius="lg"
                p="6"
                boxShadow="lg"
                gap={6}
              >
                <Box fontSize="lg" fontWeight="semibold" lineHeight="1.75rem">
                  {t('integrations facebook preview')}
                </Box>
                <Flex justifyContent="center">{preview}</Flex>
              </Flex>
            </Flex>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
